/* Help Center: forgiving search, popular chips, deep links and the current-topic highlight.
 *
 * Search is not a form (the portal script treats every form submit as its own and showed
 * "Something went wrong"). It ranks answers instead of needing an exact phrase: filler words
 * are ignored, word endings are loosened (ship, shipping, shipped), and everyday words map
 * to the words the answers use (return -> refund, login -> sign in, bill -> charge).
 */
(() => {
  'use strict';
  const root = document.querySelector('.pk-help');
  if (!root) return;

  const input = root.querySelector('[data-pk-help-search]');
  const groups = [...root.querySelectorAll('[data-pk-help-group]')];
  const items = [...root.querySelectorAll('[data-pk-help-q]')];
  const empty = root.querySelector('[data-pk-help-empty]');
  const results = root.querySelector('[data-pk-help-results]');
  const resultsList = root.querySelector('[data-pk-help-results-list]');
  const resultsTitle = root.querySelector('[data-pk-help-results-title]');
  const resultsSub = root.querySelector('[data-pk-help-results-sub]');
  const status = root.querySelector('[data-pk-help-status]');
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

  const STOP = new Set('a an the i im i\'m my me we our us you your it its is are was were be been am do does did doing can could should would will shall may might must to of for and or but if so on in at by with from about as into up out off than then that this these those there here what whats what\'s when where why how who which get got have has had need want please just really any some much many more most very also too not no yes ok okay hi hello'.split(' '));

  /* Everyday words mapped to the stems the answers use. */
  const SYNONYMS = {
    return: ['refund', 'guarante'], refund: ['refund', 'guarante'], money: ['refund'], reimburs: ['refund'], guarante: ['guarante', 'refund'], satisf: ['guarante', 'refund'],
    ship: ['ship', 'deliver', 'arriv', 'track', 'packag'], deliver: ['deliver', 'arriv', 'ship'], arriv: ['arriv', 'deliver'], packag: ['packag', 'box', 'arriv'], box: ['box', 'packag'], mail: ['ship', 'deliver'], late: ['late', 'track', 'moved'], lost: ['arriv', 'missing', 'damag'], missing: ['missing', 'arriv'], stolen: ['arriv', 'missing'],
    track: ['track', 'ship'], where: ['track'], status: ['track'], address: ['address'],
    cancel: ['cancel'], stop: ['cancel'], quit: ['cancel'], end: ['cancel'], unsubscrib: ['cancel'], leav: ['cancel'], pause: ['cancel', 'paus'],
    price: ['cost', 'price', '29.99', 'pay'], cost: ['cost', 'price'], fee: ['cost', 'fee', 'charg'], expensiv: ['cost', 'price'], cheap: ['price', '50%'],
    charg: ['charg', 'bill', 'pay'], bill: ['charg', 'bill'], pay: ['pay', 'charg', 'payment'], payment: ['payment', 'pay', 'charg'], card: ['card', 'payment'], credit: ['card', 'payment'], paypal: ['payment'], secur: ['secur', 'encrypt'], safe: ['safe', 'secur'],
    login: ['sign', 'link', 'email'], log: ['sign', 'link'], signin: ['sign'], sign: ['sign'], password: ['sign', 'link', 'code'], account: ['account', 'sign'], email: ['email', 'link'], code: ['code', 'sign'], link: ['link', 'sign'],
    filter: ['filter'], hepa: ['hepa', 'filter'], replac: ['replac', 'filter', 'swap'], chang: ['chang', 'swap', 'replac'], swap: ['swap', 'replac'], clean: ['dust', 'clean', 'filter'], dust: ['dust'],
    purifier: ['purifier'], machin: ['purifier'], unit: ['purifier', 'unit'], devic: ['purifier'], work: ['work', 'filtration'], room: ['room'], size: ['room', 'cover'], big: ['room', 'cover'],
    loud: ['loud', 'quiet', 'sound'], nois: ['loud', 'quiet', 'sound'], quiet: ['quiet', 'sound'], sound: ['sound', 'quiet'], hot: ['heat', 'hot'], heat: ['heat'], water: ['water'],
    club: ['club', 'member'], pup: ['pup', 'club'], member: ['member', 'club'], membership: ['member', 'club'], subscript: ['member', 'club', 'subscription'], subscrib: ['member', 'club'], vip: ['club', 'member'], join: ['join', 'club'],
    trial: ['trial'], free: ['free', 'trial', 'gift'], discount: ['50%', 'half', 'member'], sale: ['50%', 'half'], half: ['half', '50%'], off: ['50%'], save: ['50%', 'half'],
    gift: ['gift', 'ebook', 'toy', 'glov'], ebook: ['ebook', 'gift'], toy: ['toy', 'gift'],
    broken: ['damag', 'defect'], broke: ['damag', 'defect'], damag: ['damag', 'defect'], defect: ['defect', 'damag'], wrong: ['wrong', 'damag', 'recogn'],
    dog: ['dog', 'pet'], cat: ['cat', 'pet'], pet: ['pet', 'dog'], puppy: ['dog', 'pet'], smell: ['odor', 'smell'], odor: ['odor', 'smell'], stink: ['odor', 'smell'], pee: ['pee', 'odor'],
    color: ['color', 'black', 'white'], colour: ['color'], black: ['black', 'color'], white: ['white', 'color'],
  };

  const stem = (word) => {
    let w = word.replace(/'s$/, '');
    if (w.length > 5 && /ing$/.test(w)) w = w.slice(0, -3);
    else if (w.length > 4 && /ed$/.test(w)) w = w.slice(0, -2);
    else if (w.length > 4 && /ies$/.test(w)) w = w.slice(0, -3) + 'y';
    else if (w.length > 4 && /(ches|shes|sses|xes)$/.test(w)) w = w.slice(0, -2);
    else if (w.length > 3 && /s$/.test(w) && !/ss$/.test(w)) w = w.slice(0, -1);
    if (w.length > 5 && /e$/.test(w)) w = w.slice(0, -1);
    return w;
  };
  const words = (text) => text.toLowerCase().replace(/[’']/g, "'").replace(/[^a-z0-9%.' ]+/g, ' ').split(/\s+/).map((w) => w.replace(/^[.']+|[.']+$/g, '')).filter(Boolean);
  const stems = (text) => words(text).map(stem);

  /* Index each answer once: stems of the question and of the answer body. */
  const index = items.map((item) => {
    const summary = item.querySelector('summary');
    const answer = item.querySelector('.pk-help__a');
    return { item, q: stems(summary ? summary.textContent : ''), a: stems(answer ? answer.textContent : '') };
  });

  const SYN_KEYS = Object.keys(SYNONYMS);
  const synCache = new Map();
  function synonymsFor(term) {
    if (synCache.has(term)) return synCache.get(term);
    const out = new Set();
    SYN_KEYS.forEach((key) => {
      if (term === key || (key.length >= 3 && term.startsWith(key)) || (term.length >= 4 && key.startsWith(term))) SYNONYMS[key].forEach((v) => out.add(v));
    });
    const list = [...out];
    synCache.set(term, list);
    return list;
  }
  const matches = (list, needle) => list.some((w) => w === needle || (needle.length >= 3 && w.startsWith(needle)) || (w.length >= 4 && needle.startsWith(w)));

  /* Rare words count more than words most answers share (purifier, order, club). */
  const idfCache = new Map();
  function idf(term) {
    if (idfCache.has(term)) return idfCache.get(term);
    const options = [term, ...synonymsFor(term)];
    const df = index.filter((entry) => options.some((o) => matches(entry.q, o) || matches(entry.a, o))).length;
    const value = df ? Math.log(1 + index.length / df) : 0;
    idfCache.set(term, value);
    return value;
  }

  /* The questions people ask most, phrased the way they type them. */
  const INTENTS = [
    [/\b(how long|when)\b.*\b(arriv|deliver|get here|get it|come|ship)|delivery time|how fast/, ['q-how-long-delivery']],
    [/\b(return|money back|refund|send it back)\b/, ['q-guarantee', 'q-request-refund']],
    [/\b(how much|price|cost|fee)\b.*\b(pup club|club|membership|member)\b|\b(pup club|membership)\b.*\b(cost|price|how much)\b/, ['q-pup-club-cost']],
    [/\b(how much|cost|price)\b.*\bship|\bship\w*\b.*\b(cost|price|how much)\b/, ['q-shipping-cost']],
    [/\bwhere\b.*\b(order|package|stuff)\b|\btrack/, ['q-track-order']],
    [/\b(cancel|stop|end|quit|unsubscribe)\b.*\b(club|membership|subscription|trial)\b|\b(club|membership|subscription|trial)\b.*\b(cancel|stop|end)\b/, ['q-cancel-pup-club']],
    [/\b(twice|double|don.?t recogni|unrecogni|unknown charge|wrong charge|fraud)/, ['q-payment-problem']],
    [/\b(password|log ?in|sign ?in|can.?t get in)\b/, ['q-sign-in', 'q-link-expired']],
    [/\b(how often|when)\b.*\b(filter|replace|change)\b/, ['q-replace-often']],
  ];

  function score(entry, terms) {
    let total = 0;
    let hit = 0;
    terms.forEach((term) => {
      const options = [term, ...synonymsFor(term)];
      let best = 0;
      options.forEach((option, i) => {
        const weight = i === 0 ? 1 : 0.8;
        if (matches(entry.q, option)) best = Math.max(best, 3 * weight);
        else if (matches(entry.a, option)) best = Math.max(best, 1 * weight);
      });
      if (best > 0) hit += 1;
      total += best * idf(term);
    });
    /* Answers that cover more of what was typed rank first. */
    return hit ? total * (0.5 + hit / terms.length) : 0;
  }

  const clones = [];
  function clearResults() {
    clones.forEach((node) => node.remove());
    clones.length = 0;
  }

  function showAll() {
    root.classList.remove('is-searching');
    clearResults();
    if (results) results.hidden = true;
    if (empty) empty.hidden = true;
    groups.forEach((group) => { group.hidden = false; });
    items.forEach((item) => { item.hidden = false; item.open = false; item.classList.remove('is-first'); });
    if (status) status.textContent = '';
  }

  let lastQuery = '';
  function search() {
    const raw = input.value.trim();
    if (raw === lastQuery) return;
    lastQuery = raw;
    const terms = [...new Set(words(raw).filter((w) => !STOP.has(w) && w.length > 1).map(stem))];
    if (!terms.length) { showAll(); return; }

    const phrase = ' ' + words(raw).join(' ') + ' ';
    const boosted = new Set();
    INTENTS.forEach(([pattern, ids]) => { if (pattern.test(phrase)) ids.forEach((id, i) => boosted.add(id + ':' + i)); });
    const boostFor = (id) => { for (const b of boosted) { const [bid, i] = b.split(':'); if (bid === id) return 12 - Number(i); } return 0; };
    const ranked = index.map((entry) => ({ entry, s: score(entry, terms) + boostFor(entry.item.id) }))
      .filter((r) => r.s >= 1)
      .sort((a, b) => b.s - a.s)
      .slice(0, 8);

    clearResults();
    root.classList.add('is-searching');
    groups.forEach((group) => { group.hidden = true; });
    if (!ranked.length) {
      if (results) results.hidden = true;
      if (empty) empty.hidden = false;
      if (status) status.textContent = 'No answers match that yet.';
      return;
    }
    if (empty) empty.hidden = true;
    ranked.forEach((r, i) => {
      const node = r.entry.item.cloneNode(true);
      node.removeAttribute('id');
      node.removeAttribute('data-pk-help-q');
      node.hidden = false;
      node.open = i === 0;
      node.classList.toggle('is-first', i === 0);
      node.dataset.pkHelpSource = r.entry.item.id;
      resultsList.appendChild(node);
      clones.push(node);
    });
    if (resultsTitle) resultsTitle.textContent = ranked.length === 1 ? 'Top answer' : 'Top answers';
    if (resultsSub) resultsSub.textContent = `For “${raw}”`;
    if (results) results.hidden = false;
    if (status) status.textContent = `${ranked.length} ${ranked.length === 1 ? 'answer' : 'answers'} found.`;
  }

  let timer = 0;
  input?.addEventListener('input', () => { clearTimeout(timer); timer = setTimeout(search, 120); });
  input?.addEventListener('search', () => { lastQuery = null; search(); });
  input?.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') { input.value = ''; lastQuery = ''; showAll(); return; }
    if (event.key !== 'Enter') return;
    event.preventDefault();
    lastQuery = null;
    search();
    const target = results && !results.hidden ? results : empty;
    target?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  });

  const openTarget = (id) => {
    const item = id && document.getElementById(id);
    if (!item || !item.matches('details')) return;
    item.open = true;
    item.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  };

  root.querySelectorAll('[data-pk-help-open]').forEach((chip) => chip.addEventListener('click', (event) => {
    event.preventDefault();
    if (input && input.value) { input.value = ''; lastQuery = ''; showAll(); }
    openTarget(chip.dataset.pkHelpOpen);
    history.replaceState(null, '', '#' + chip.dataset.pkHelpOpen);
  }));
  root.querySelectorAll('.pk-help__topics a, .pk-help__nav a').forEach((link) => link.addEventListener('click', () => {
    if (input && input.value) { input.value = ''; lastQuery = ''; showAll(); }
  }));
  if (location.hash.startsWith('#q-')) openTarget(location.hash.slice(1));

  const links = [...root.querySelectorAll('.pk-help__nav a')];
  if ('IntersectionObserver' in window && links.length) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        links.forEach((link) => link.classList.toggle('is-current', link.getAttribute('href') === '#' + entry.target.id));
      });
    }, { rootMargin: '-20% 0px -70% 0px' });
    groups.forEach((group) => observer.observe(group));
  }
})();
